import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Lock, ArrowRight, Wind } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { HERO_IMAGE, intro, sections } from '../data/survivingToThriving';

const SectionCard = ({ s, open, onToggle, unlocked }) => {
  const locked = !s.free && !unlocked;
  return (
    <div
      className="bg-white rounded-2xl border border-stone-100 overflow-hidden transition-shadow"
      style={{ boxShadow: open ? '0 8px 24px -14px rgba(58,74,58,0.35)' : 'none' }}
      data-testid={`section-${s.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-4 sm:p-5 text-left"
        data-testid={`section-toggle-${s.id}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-heading text-xl sm:text-2xl text-forest">{s.title}</h3>
            {locked && <Lock size={14} className="text-stone-400" />}
          </div>
          {s.tagline && <p className="font-body text-stone-500 text-sm italic">{s.tagline}</p>}
        </div>
        <ChevronDown
          size={20}
          className="text-stone-400 transition-transform flex-shrink-0"
          style={{ transform: open ? 'rotate(180deg)' : 'none' }}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden"
            data-testid={`section-panel-${s.id}`}
          >
            <div className="px-4 sm:px-5 pb-5 pt-1">
              {locked ? (
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-forest/5 border border-forest/10 rounded-xl p-4">
                  <p className="flex-1 font-body text-sm text-stone-600">
                    <Lock size={13} className="inline mr-1.5 -mt-0.5 text-stone-400" />
                    This chapter is part of the full <em>Surviving to Thriving</em> journey.
                  </p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-1.5 bg-forest text-cream font-body text-sm px-4 py-2 rounded-full hover:bg-forest-dark transition-colors whitespace-nowrap"
                    data-testid={`section-unlock-${s.id}`}
                  >
                    Unlock the journey <ArrowRight size={14} />
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {s.intro && <p className="font-body text-stone-600 text-sm leading-relaxed">{s.intro}</p>}
                  {s.paragraphs?.map((p, i) => (
                    <p key={i} className="font-body text-stone-600 text-sm leading-relaxed">{p}</p>
                  ))}
                  {s.list && (
                    <ul className="space-y-2">
                      {s.list.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 bg-sage/5 rounded-xl px-3 py-2.5">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-terracotta flex-shrink-0" />
                          {typeof item === 'string' ? (
                            <span className="font-body text-sm text-stone-600">{item}</span>
                          ) : (
                            <span className="font-body text-sm text-stone-600">
                              <strong className="text-forest">{item.title}.</strong> {item.text}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.links && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {s.links.map((l, i) => (
                        <Link
                          key={i}
                          to={l.to}
                          className="inline-flex items-center gap-1.5 bg-forest/5 text-forest font-body text-xs font-medium px-3 py-2 rounded-full hover:bg-forest/10 transition-colors"
                          data-testid={`section-link-${s.id}-${i}`}
                        >
                          {l.label} <ArrowRight size={12} />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SurvivingToThrivingPage = () => {
  const { hasAccess } = useAuth();
  const unlocked = hasAccess('surviving-to-thriving');
  const [openId, setOpenId] = useState(sections[0].id);

  const toggle = (id) => setOpenId((cur) => (cur === id ? null : id));

  return (
    <div className="bg-cream min-h-screen pt-24 pb-16" data-testid="surviving-to-thriving-page">
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-6" data-testid="back-to-programs">
          <ArrowLeft size={18} />
          <span className="font-body text-sm">Back to Programs</span>
        </Link>

        {/* Hero */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <img
            src={HERO_IMAGE}
            alt="From surviving to thriving"
            className="w-full rounded-2xl shadow-lg mb-6"
            data-testid="hero-image"
          />
          <p className="font-body text-terracotta text-sm tracking-wide uppercase mb-1">{intro.eyebrow}</p>
          <h1 className="font-heading text-4xl sm:text-5xl text-forest mb-2">{intro.title}</h1>
          <p className="font-body text-stone-600 text-base sm:text-lg mb-4">{intro.tagline}</p>
          <div className="bg-white rounded-xl border border-stone-100 p-4 flex gap-3">
            <Wind size={18} className="text-sage flex-shrink-0 mt-0.5" />
            <p className="font-body text-forest text-sm italic">{intro.note}</p>
          </div>
        </motion.div>

        {/* Sections */}
        <div className="mb-10 flex items-baseline justify-between">
          <h2 className="font-heading text-2xl text-forest">The Journey</h2>
          <span className="font-body text-xs text-stone-400 italic">Tap any chapter to open</span>
        </div>
        <div className="space-y-3">
          {sections.map((s) => (
            <SectionCard key={s.id} s={s} open={openId === s.id} onToggle={() => toggle(s.id)} unlocked={unlocked} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SurvivingToThrivingPage;
